import { FunctionComponent } from "react";
import { Control, Controller, FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFilterPropertyOptions } from "../hooks/useFilterPropertyOptions";
import { ReengageOptionItem, ReengageSourceFormValues } from "../schema/reengageSource";
import { ReengageMultiSelect } from "./ReengageMultiSelect";

interface ReengageDropdownFieldProps {
	control: Control<ReengageSourceFormValues>;
	name: FieldPath<ReengageSourceFormValues>;
	label: string;
	placeholder: string;
	/** When set, options are fetched from the backend via getFilterByProperty. */
	propertyName?: string;
	/** Static option list (used when propertyName is not provided). */
	staticOptions?: ReengageOptionItem[];
	listId?: number;
	hint?: string;
}

const FieldLabel: FunctionComponent<{ label: string; hint?: string }> = ({ label, hint }) => (
	<div className="flex items-center justify-between mb-1.5">
		<span className="text-[13px] font-medium text-text">{label}</span>
		{hint && <span className="text-[11px] text-text-meta">{hint}</span>}
	</div>
);

/**
 * One labelled multi-select bound to a re-engagement form array field. Sources its options either
 * from a static list or lazily from `getFilterByProperty` when `propertyName` is given.
 */
export const ReengageDropdownField: FunctionComponent<ReengageDropdownFieldProps> = ({
	control,
	name,
	label,
	placeholder,
	propertyName,
	staticOptions,
	listId,
	hint,
}) => {
	const { t } = useTranslation();
	const { options, isLoading, search } = useFilterPropertyOptions(propertyName ?? "", listId);

	const data = propertyName ? options : (staticOptions ?? []);

	return (
		<div className="mb-4">
			<FieldLabel hint={hint ?? t("list.reengage.multiSelect")} label={label} />
			<Controller
				control={control}
				name={name}
				render={({ field }) => (
					<ReengageMultiSelect
						loading={propertyName ? isLoading : false}
						options={data}
						placeholder={placeholder}
						testId={`reengage-${name}`}
						value={(field.value as ReengageOptionItem[]) ?? []}
						onChange={field.onChange}
						onSearch={propertyName ? search : undefined}
					/>
				)}
			/>
		</div>
	);
};
